import { Injectable, signal } from "@angular/core";
import { Store, select } from '@ngrx/store';
import { IAppState } from '../../state/app.state';
import * as TransactionsSelector from '../selectors/transactions.selectors';
import * as TransactionTypesSelector from '../selectors/transaction-types.selector';
import * as TransactionsActions from '../actions/transactions.actions';
import { ITransactionSearchParam, ITransactionActionType, ITransactionTypeParam } from 'src/app/models/transactions';
import { initialTransactionsState, ITransactionsState } from '../../state/transactions-state';
import { IGeneralSelectionState } from '../../state/general-selection.state';
import { initialPagingInfo } from 'src/app/models/pagingInfo';
import { Observable, of, Subscription } from "rxjs";
import { ClientEntity } from 'src/app/models/clients';
import { GeneralSelectionFacade } from '../../general-selection/facade/general-selection.facade';
import { areArraysUnequal } from 'src/app/utils/comparative.methods';
import { IPortfolio } from "../../../models/portfolios";
import { AccountType } from 'src/app/models/account';
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { TransactionsService } from "../../../services/http/transactions.services";
import { catchError, tap } from "rxjs/operators";
import { SnackBarService } from "../../../services/snackbar/snackBar.service";
import { FeatureFlagsService } from "../../../services/feature-flags/feature-flags.service";

@Injectable()
@UntilDestroy()
export class TransactionTypeFacade {

    public selectedCashTransactionType$ = this.store.pipe(untilDestroyed(this), select(TransactionTypesSelector.getCashSelectedTransactionType));
    public selectedRevenueTransactionType$ = this.store.pipe(untilDestroyed(this), select(TransactionTypesSelector.getRevenueSelectedTransactionType));
    public selectedSecurityTransactionType$ = this.store.pipe(untilDestroyed(this), select(TransactionTypesSelector.getSecuritySelectedTransactionType));


    public transactionTypes = signal<{ [key in AccountType]: ITransactionActionType[] }>({
        [AccountType.Cash]: undefined,
        [AccountType.Securities]: undefined,
        [AccountType.Revenue]: undefined
    });


    public entityDateSubscription: Subscription;
    public generalSelectionState: IGeneralSelectionState;

    selectedEntities: ClientEntity[] = [];
    selectedPortfolios: IPortfolio[] = [];
    date: Date = null;

    constructor(protected store: Store<IAppState>, private generalSelectionFacade: GeneralSelectionFacade, private transactionsService: TransactionsService,
                private snackBarService: SnackBarService){
        
        this.entityDateSubscription = this.generalSelectionFacade.getState()
            .pipe(untilDestroyed(this))
            .subscribe((state: IGeneralSelectionState) => {
                this.generalSelectionState = state;
                const selectedEntitiesChanged = areArraysUnequal(this.selectedEntities, state.selectedEntities, "EntityId");
                const selectedPortfoliosChanged = areArraysUnequal(this.selectedPortfolios, state.selectedPortfolios, "id");

                if(this.date !== state.toDate || selectedEntitiesChanged || selectedPortfoliosChanged){
                    this.selectedEntities = this.generalSelectionState.selectedEntities;
                    this.selectedPortfolios = this.generalSelectionState.selectedPortfolios;
                    this.date = this.generalSelectionState.toDate;

                    if (this.generalSelectionState.selectedEntities == null || this.generalSelectionState.selectedEntities.length == 0) return;

                    this.transactionTypes.set({
                        [AccountType.Cash]: undefined,
                        [AccountType.Securities]: undefined,
                        [AccountType.Revenue]: undefined
                    });
                }
        });
    }

    public selectCashTransactionType(transactionType: ITransactionActionType): void{
        this.store.dispatch(new TransactionsActions.SelectCashTransactionType(transactionType));
    }

    public selectRevenueTransactionType(transactionType: ITransactionActionType): void {
        this.store.dispatch(new TransactionsActions.SelectRevenueTransactionType(transactionType));
    }

    public selectSecurityTransactionType(transactionType: ITransactionActionType): void {
        this.store.dispatch(new TransactionsActions.SelectSecurityTransactionType(transactionType));
    }

    public getTransactionTypes(accountType: AccountType) : Observable<ITransactionActionType[]> {
        let transactionTypes = this.transactionTypes();
        
        if(transactionTypes[accountType] === undefined){
            return this.transactionsService.getTransactionTypes({
                toDate: this.date,
                entities: this.selectedEntities,
                portfolios: this.selectedPortfolios,
                accountType: accountType
            } as ITransactionTypeParam)
                .pipe(tap((transactionActions) => {
                transactionTypes[accountType] = transactionActions;
                this.transactionTypes.set(transactionTypes);
            }), 
            catchError((_) => {
                this.snackBarService.showWarningSnackBar("Failed to retrieve transaction types relevant to your portfolio");
                
                return of([]);
            }));
        }
        
        return of(transactionTypes[accountType]);
    }
}

export abstract class TransactionsFacade {

    protected dispatchGetTransactions: (transactionSearchParam: ITransactionSearchParam) => void = (_) => {};
    protected dispatchResetTransactions: () => void = () => {};

    private generalSelectionState: IGeneralSelectionState;
    private currentTransactionState: ITransactionsState = initialTransactionsState;

    constructor(protected store: Store<IAppState>,
        public currentTransactionsObservable$: Observable<ITransactionsState>,
        protected portfolioTransactionType: AccountType,
        private featureFlagsService: FeatureFlagsService) {
        this.initialiseData();
    }

    private initialiseData() {
        this.subscribeToTransactionsState();
        this.subscribeToGeneralSelection();
    }

    public resetTransactions() {
        this.dispatchResetTransactions();
    }

    // Fetch transaction data using the general selection dates
    public getTransactionsUsingGeneralSelectionDates(transactionActionType: ITransactionActionType, isNextPage?: boolean) {
        if (this.generalSelectionState.selectedEntities.length == 0) return;

        let state = this.currentTransactionState;
        let searchedParam: ITransactionSearchParam = {
            fromDate: (this.generalSelectionState.fromDate) ? this.generalSelectionState.fromDate : new Date(0),
            toDate: (this.generalSelectionState.toDate) ? this.generalSelectionState.toDate : new Date(0),
            pagingInfo: isNextPage ? { ...state.pagingInfo } : { ...this.initializePagingInfo() },
            transactionActionType: transactionActionType,
            accountType: this.portfolioTransactionType,
            entities: this.generalSelectionState.selectedEntities,
            portfolios: this.generalSelectionState.selectedPortfolios
        };

        // This is a temporary control to enable/disable the widget if we experience high load and need reduce impact on
        // operations. This will be removed in OB-5120
        this.featureFlagsService.getFeatureFlag('enable-transactions-tab').subscribe((isEnabled) => {
            if(isEnabled){
                this.dispatchGetTransactions(searchedParam);
            }
        });
    }

    public getTransactionsUsingExplicitDates(fromDate: Date, toDate: Date, transactionActionType: ITransactionActionType, isNextPage?: boolean){
        let state = this.currentTransactionState;
        let searchedParam: ITransactionSearchParam = {
            fromDate: fromDate,
            toDate: toDate,
            pagingInfo: isNextPage ? { ...state.pagingInfo } : { ...this.initializePagingInfo() },
            transactionActionType: transactionActionType,
            accountType: this.portfolioTransactionType,
            entities: this.generalSelectionState.selectedEntities,
            portfolios: this.generalSelectionState.selectedPortfolios
        };

        this.dispatchGetTransactions(searchedParam);
    }

    private subscribeToGeneralSelection() {
        this.store
            .select('generalSelection').pipe(
            untilDestroyed(this))
            .subscribe((state: IGeneralSelectionState) => {

            let selectedEntitiesChanged = true;
            let selectedPortfoliosChanged = true;


            if(this.generalSelectionState){
                selectedEntitiesChanged = areArraysUnequal(this.generalSelectionState.selectedEntities, state.selectedEntities, "EntityId");
                selectedPortfoliosChanged = areArraysUnequal(this.generalSelectionState.selectedPortfolios, state.selectedPortfolios, "id");
            }

            this.generalSelectionState = state;
            if(selectedEntitiesChanged || selectedPortfoliosChanged){
                this.resetTransactions();
                this.getTransactionsUsingGeneralSelectionDates(this.currentTransactionState.transactionActionType, false);
            }
        });
    }

    private subscribeToTransactionsState() {
        this.currentTransactionsObservable$.pipe(
            untilDestroyed(this)).subscribe((transactionsState: ITransactionsState) => {
            if (transactionsState) {
                this.currentTransactionState = transactionsState;
            }
        })
    }

    private initializePagingInfo() {
        return {
            ...initialPagingInfo,
            sortOrder: "Date",
            pageSize: 100
        };
    }
}

@Injectable()
@UntilDestroy()
export class CashTransactionsFacade extends TransactionsFacade {

    constructor(protected store: Store<IAppState>, featureFlagsService: FeatureFlagsService){
        super(store,
            store.pipe(select(TransactionsSelector.selectCashTransactionsState)),
            AccountType.Cash,
            featureFlagsService);
        this.dispatchResetTransactions = () => this.store.dispatch(new TransactionsActions.ResetCashTransactions());
        this.dispatchGetTransactions = (searchParams) => this.store.dispatch(new TransactionsActions.GetCashTransactions(searchParams));
    }
}

@Injectable()
@UntilDestroy()
export class SecurityTransactionsFacade extends TransactionsFacade {

    constructor(protected store: Store<IAppState>, featureFlagsService: FeatureFlagsService) {
        super(store,
            store.pipe(select(TransactionsSelector.selectSecuritiesTransactionsState)),
            AccountType.Securities,
            featureFlagsService);
        this.dispatchGetTransactions = (searchParams) => this.store.dispatch(new TransactionsActions.GetSecurityTransactions(searchParams));
        this.dispatchResetTransactions = () => this.store.dispatch(new TransactionsActions.ResetSecuritiesTransactions());
    }
}

@Injectable()
@UntilDestroy()
export class RevenueTransactionsFacade extends TransactionsFacade {

    constructor(protected store: Store<IAppState>, featureFlagsService: FeatureFlagsService) {
        super(store,
            store.pipe(select(TransactionsSelector.selectRevenueTransactionsState)),
            AccountType.Revenue,
            featureFlagsService);
        this.dispatchGetTransactions = (searchParams) => this.store.dispatch(new TransactionsActions.GetRevenueTransactions(searchParams));
        this.dispatchResetTransactions = () => this.store.dispatch(new TransactionsActions.ResetRevenueTransactions());
    }
}
